// src/components/layout/SiteFooter.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    MailOutlined,
    PhoneOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    TwitterOutlined,
    InstagramOutlined,
    LinkedinOutlined,
    SendOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';

import { ROUTES } from '../../constants/routes';
import supabase from '../../lib/supabase';

const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

export default function SiteFooter() {
    const intl = useIntl();
    const year = new Date().getFullYear();

    const [email, setEmail] = useState('');
    const [subscribing, setSubscribing] = useState(false);

    /* ---------------- newsletter ---------------- */
    const onSubscribe = async (e) => {
        e.preventDefault();
        const clean = email.trim().toLowerCase();

        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
            toast.error(intl.formatMessage({ id: 'validation.email' }));
            return;
        }

        setSubscribing(true);
        try {
            const { error } = await supabase
                .from('newsletter_subscribers')
                .upsert({ email: clean }, { onConflict: 'email' });

            if (error) throw error;

            toast.success(intl.formatMessage({ id: 'footer.newsletter.success' }));
            setEmail('');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[SiteFooter] newsletter insert failed:', err?.message);
            toast.success(intl.formatMessage({ id: 'footer.newsletter.success' }));
            setEmail('');
        } finally {
            setSubscribing(false);
        }
    };

    /* ---------------- paystar badge ---------------- */
    const openPaystarBadge = () => {
        window.open(
            'https://my.paystar.ir/namad/j8elq',
            '_blank',
            'location=yes,height=570,width=520,scrollbars=yes,status=yes'
        );
    };

    /* ---------------- render ---------------- */
    return (
        <footer className="site-footer">
            <div className="site-footer__inner">
                {/* Brand column */}
                <div className="col col--brand">
                    <Link to="/" className="brand">
                        SOHO<span>CARD</span>
                    </Link>

                    <p className="tagline">
                        <FormattedMessage id="footer.tagline" />
                    </p>

                    <p className="about-text">
                        <FormattedMessage id="footer.about.text" />
                    </p>

                    <ul className="contact-list">
                        <li>
                            <MailOutlined />
                            <a href="mailto:info@sohocard.ir">info@sohocard.ir</a>
                        </li>
                        <li>
                            <PhoneOutlined />
                            <a href="tel:+989211297527" dir="ltr">
                                <FormattedMessage id="footer.contact.phone" />
                            </a>
                        </li>
                        <li>
                            <PhoneOutlined />
                            <a href="tel:+989225225128" dir="ltr">
                                <FormattedMessage id="footer.contact.phone2" />
                            </a>
                        </li>
                        <li>
                            <PhoneOutlined />
                            <a href="tel:+984142250318" dir="ltr">
                                <FormattedMessage id="footer.contact.phone3" />
                            </a>

                        </li>
                        <li>
                            <EnvironmentOutlined />
                            <span><FormattedMessage id="footer.contact.address" /></span>
                        </li>
                        <li>
                            <ClockCircleOutlined />
                            <span><FormattedMessage id="footer.contact.hours" /></span>
                        </li>
                    </ul>

                    <div className="social">
                        <a href="https://twitter.com/sohocard" target="_blank" rel="noreferrer" aria-label="Twitter">
                            <TwitterOutlined />
                        </a>
                        <a href="https://instagram.com/sohocard" target="_blank" rel="noreferrer" aria-label="Instagram">
                            <InstagramOutlined />
                        </a>
                        <a href="https://linkedin.com/company/sohocard" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                            <LinkedinOutlined />
                        </a>
                        <a href="https://t.me/sohocard" target="_blank" rel="noreferrer" aria-label="Telegram">
                            <SendOutlined />
                        </a>
                    </div>
                </div>

                {/* Products */}
                <div className="col">
                    <h4><FormattedMessage id="footer.products" /></h4>
                    <ul>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.giftCards" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.visa" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.mastercard" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.wallets" /></Link></li>
                        <li><Link to={ROUTES.MARKET}><FormattedMessage id="footer.products.prepaid" /></Link></li>
                    </ul>
                </div>

                {/* Company */}
                <div className="col">
                    <h4><FormattedMessage id="footer.company" /></h4>
                    <ul>
                        <li><Link to={ROUTES.ABOUT}><FormattedMessage id="footer.company.about" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.company.contact" /></Link></li>
                        <li><a href="https://blog.sohocard.ir" target="_blank" rel="noreferrer"><FormattedMessage id="footer.company.blog" /></a></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.company.partners" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.company.careers" /></Link></li>
                    </ul>
                </div>

                {/* Support */}
                <div className="col">
                    <h4><FormattedMessage id="footer.support" /></h4>
                    <ul>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.support.help" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.support.faq" /></Link></li>
                        <li><a href="https://status.sohocard.ir" target="_blank" rel="noreferrer"><FormattedMessage id="footer.support.status" /></a></li>
                        <li><Link to={ROUTES.ORDER_HISTORY}><FormattedMessage id="footer.support.track" /></Link></li>
                        <li><Link to={ROUTES.CONTACT}><FormattedMessage id="footer.support.contact" /></Link></li>
                        <li><Link to={ROUTES.HELP}><FormattedMessage id="footer.support.help" /></Link></li>
                    </ul>
                </div>


                <div className="col">
                    <h4><FormattedMessage id="footer.legal" /></h4>
                    <ul>
                        <li><Link to={ROUTES.TERMS}><FormattedMessage id="footer.legal.terms" /></Link></li>
                        <li><Link to={ROUTES.PRIVACY}><FormattedMessage id="footer.legal.privacy" /></Link></li>
                        <li><Link to="/refund"><FormattedMessage id="footer.legal.refund" /></Link></li>
                        <li><Link to="/compliance"><FormattedMessage id="footer.legal.compliance" /></Link></li>
                        <li><Link to="/cookies"><FormattedMessage id="footer.legal.cookies" /></Link></li>
                    </ul>



                    {/* Payment methods */}
                    <div className="payments">
                        <div className="pm-title"><FormattedMessage id="footer.payments.title" /></div>
                        <div className="pm-chips">
                            <span className="pm-chip"><FormattedMessage id="footer.payments.paystar" /></span>
                            {/* <span className="pm-chip"><FormattedMessage id="footer.payments.zarinpal" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.idpay" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.nextpay" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.zibal" /></span>
                            <span className="pm-chip"><FormattedMessage id="footer.payments.manual" /></span> */}
                        </div>
                    </div>

                    {/* Trust badges */}
                    <div className="trust-badges">
                        {/* PayStar */}
                        <button
                            type="button"
                            className="trust-badge"
                            onClick={openPaystarBadge}
                            aria-label="PayStar"
                        >
                            <img
                                width="100"
                                height="130"
                                src="https://panel.paystar.ir/storage/namad/namadStar2.png?expires=2106827017&signature=8d5704d7ed7aea4a2ea67b72a7c1cf49"
                                alt="نماد پی‌استار"
                            />
                        </button>


                        {/* Enamad */}
                        <a referrerpolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=8020445&Code=ZTax34l39656tO2LlUYPB775ktpyFPaE'>
                            <img referrerpolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=8020445&Code=ZTax34l39656tO2LlUYPB775ktpyFPaE' alt='اینماد' style={{ cursor: 'pointer' }} code='ZTax34l39656tO2LlUYPB775ktpyFPaE' />
                        </a>
                    </div>
                </div>
                {/* Legal */}
                <div className="col">
                    {/* Newsletter */}
                    <div className="newsletter">
                        <div className="nl-title"><FormattedMessage id="footer.newsletter.title" /></div>
                        <div className="nl-sub"><FormattedMessage id="footer.newsletter.sub" /></div>

                        <form className="nl-form" onSubmit={onSubscribe}>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={intl.formatMessage({ id: 'footer.newsletter.placeholder' })}
                                disabled={subscribing}
                                required
                            />
                            <button type="submit" disabled={subscribing}>
                                <FormattedMessage id="footer.newsletter.cta" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Bottom bar */}
            <div className="site-footer__bottom">
                <p className="rights">
                    <FormattedMessage id="footer.bottom.rights" values={{ year }} />
                </p>

                <div className="meta">
                    <span><FormattedMessage id="footer.bottom.madeIn" /></span>
                    <span className="dot-sep">·</span>
                    <span dir="ltr"><FormattedMessage id="footer.bottom.version" values={{ version: APP_VERSION }} /></span>
                </div>
            </div>
        </footer>
    );
}